import { Bell, CheckCircle2, Target, Ticket, Trash2, FileText, MessageSquareText, Gauge } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Notification } from '@/contexts/NotificationContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
  onDelete: (notificationId: string) => void;
}

const getIcon = (type: string) => {
  switch (type) {
    case 'lead':
      return <Target className="h-5 w-5 text-primary" />;
    case 'todo':
      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    case 'ticket':
      return <Ticket className="h-5 w-5 text-blue-600" />;
    case 'project_note':
      return <FileText className="h-5 w-5 text-purple-600" />;
    case 'feedback':
      return <MessageSquareText className="h-5 w-5 text-amber-600" />;
    case 'ems':
      return <Gauge className="h-5 w-5 text-orange-600" />;
    default:
      return <Bell className="h-5 w-5 text-muted-foreground" />;
  }
};

export const NotificationItem = ({ notification, onClick, onDelete }: NotificationItemProps) => {
  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
    locale: nl,
  });

  return (
    <div
      className={cn(
        "group relative flex gap-4 p-4 border-b border-border hover:bg-accent/50 transition-colors cursor-pointer",
        !notification.is_read && "bg-accent/20"
      )}
      onClick={() => onClick(notification)}
    >
      {/* Unread indicator */}
      {!notification.is_read && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary" />
      )}

      {/* Icon */}
      <div className="flex-shrink-0 ml-4">
        {getIcon(notification.type)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className={cn(
              "text-sm font-medium",
              !notification.is_read && "font-semibold"
            )}>
              {notification.title}
            </p>
            {notification.description && (
              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2 whitespace-pre-line">
                {notification.description}
              </p>
            )}
          </div>
          
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {timeAgo}
          </span>
        </div>
      </div>

      {/* Delete button */}
      <Button
        variant="ghost"
        size="icon"
        className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(notification.id);
        }}
      >
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>
    </div>
  );
};
